import { LocalizationPipe } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, computed, inject, signal, type WritableSignal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  DEFAULT_MAX_RESULT_COUNT,
  DEFAULT_TOASTER_LIFE,
  IWizardContext,
  PERIOD_TYPE_NAME_SILVICULTURE,
  REASON_GROUP_NAME_AREA_ADJUSTMENT,
  WizardStepComponent,
  ZendraLocalizationServices,
  ZnPill,
  ZnSelectQueryParams,
  ZnSingleSelect,
} from '@zendra/shared';
import { firstValueFrom, tap, type Observable } from 'rxjs';

import { StandCatalogs } from '../../stand/stand-creation-form/services/stand-catalogs';
import type { IAreaMaintenanceFormValue } from '../interfaces/IAreaMaintenanceFormValue';
import type { IAreaMaintenanceSelectedOptions } from '../interfaces/IAreaMaintenanceSelectedOptions';
import type { IAreaMaintenanceSelectOption } from '../interfaces/IAreaMaintenanceSelectOption';
import type { IResultingFusionStand } from '../interfaces/IResultingFusionStand';
import type { IStepCreateAreaMaintenanceData } from '../interfaces/IStepCreateAreaMaintenanceData';
import type { IStepSelectStandsToFuseData } from '../interfaces/IStepSelectStandsToFuseData';
import { StandFusionPlanningService } from '../services/stand-fusion-planning.service';
import { SelectValue } from '../stand-fusion.config';

const EMPTY_VALUE = '-----';

@Component({
  selector: 'lib-step-create-area-maintenance',
  standalone: true,
  imports: [LocalizationPipe, ReactiveFormsModule, ZnPill, ZnSingleSelect],
  templateUrl: './step-create-area-maintenance.html',
})
export class StepCreateAreaMaintenance
  implements WizardStepComponent<IStepCreateAreaMaintenanceData>
{
  private readonly formBuilder = inject(FormBuilder);
  private readonly toaster = inject(ToasterService);
  private readonly standCatalogs = inject(StandCatalogs);
  private readonly standFusionPlanningService = inject(StandFusionPlanningService);

  readonly localizationKeyPrefix = 'Geographic::StandFusion.StepCreateAreaMaintenance';
  readonly localizationResource = ZendraLocalizationServices.LanguageService;
  readonly previousStepId = 2;

  readonly resultingStand = signal<IResultingFusionStand | null>(null);
  readonly resultingStandChip = computed(() => this.resultingStand()?.standName ?? EMPTY_VALUE);
  readonly cycle = computed(() => this.resultingStand()?.cycle ?? null);
  readonly isLastCycle = computed(() => !!this.resultingStand()?.isLastCycle);
  readonly rotation = computed(() => this.resultingStand()?.rotation ?? null);
  readonly isLastRotation = computed(() => !!this.resultingStand()?.isLastRotation);
  readonly currentArea = computed(() => this.resultingStand()?.currentArea ?? null);
  readonly adjustedArea = computed(() => this.resultingStand()?.adjustedArea ?? null);
  readonly periodOptions = signal<IAreaMaintenanceSelectOption[]>([]);
  readonly reasonOptions = signal<IAreaMaintenanceSelectOption[]>([]);
  readonly adjustmentTypeOptions = signal<IAreaMaintenanceSelectOption[]>([]);
  readonly areaDelta = computed(() => {
    const currentArea = this.currentArea();
    const adjustedArea = this.adjustedArea();

    if (currentArea === null || adjustedArea === null) {
      return null;
    }

    return this.roundArea(adjustedArea - currentArea);
  });

  readonly form = this.createAreaMaintenanceForm();

  private readonly periodCatalog = new Map<string, IAreaMaintenanceSelectOption>();
  private readonly reasonCatalog = new Map<string, IAreaMaintenanceSelectOption>();
  private readonly adjustmentTypeCatalog = new Map<string, IAreaMaintenanceSelectOption>();

  readonly loadPeriod = (params?: ZnSelectQueryParams) =>
    this.loadSelectCatalog(
      params,
      normalizedParams =>
        this.standCatalogs.loadPeriod(normalizedParams, PERIOD_TYPE_NAME_SILVICULTURE),
      this.periodCatalog,
    );

  readonly loadReason = (params?: ZnSelectQueryParams) =>
    this.loadSelectCatalog(
      params,
      normalizedParams =>
        this.standCatalogs.loadReason(normalizedParams, REASON_GROUP_NAME_AREA_ADJUSTMENT),
      this.reasonCatalog,
    );

  readonly loadAdjustmentTypes = (params?: ZnSelectQueryParams) =>
    this.standCatalogs.loadAdjustmentTypes(params).pipe(
      tap(response => {
        response.items.forEach(item => {
          this.adjustmentTypeCatalog.set(item.key, item);
        });
      }),
    );

  onActivate(context: IWizardContext): void {
    context.patchStepState(context.activeStepId(), {
      visited: true,
    });

    const previousStepData =
      context.getStepState<IStepSelectStandsToFuseData>(this.previousStepId)?.data;
    const currentState =
      context.getStepState<IStepCreateAreaMaintenanceData>(context.activeStepId())?.data;
    const sourceStep2Snapshot = this.buildStep2Snapshot(previousStepData);
    const shouldRestoreCurrentData =
      !!currentState && currentState.sourceStep2Snapshot === sourceStep2Snapshot;

    this.resultingStand.set(
      previousStepData?.resultingStand ?? currentState?.resultingStand ?? null,
    );

    if (!shouldRestoreCurrentData && currentState) {
      context.patchStepState(context.activeStepId(), {
        valid: false,
        completed: false,
        data: undefined,
        summary: [],
      });
    }

    this.restoreSelectedOptions(shouldRestoreCurrentData ? currentState?.selectedOptions : undefined);
    this.form.reset(
      this.buildInitialFormValue(
        shouldRestoreCurrentData ? currentState?.maintenance : undefined,
      ),
      { emitEvent: false },
    );
  }

  async canLeave(): Promise<boolean> {
    if (!this.validateRequiredForm()) {
      return false;
    }

    const standId = Number(this.resultingStand()?.standBackendId);
    const periodId = Number(this.form.getRawValue().period);

    if (!Number.isInteger(standId) || standId <= 0 || !Number.isInteger(periodId) || periodId <= 0) {
      this.notifyRequiredFields();
      return false;
    }

    try {
      await firstValueFrom(
        this.standFusionPlanningService.ensureAreaMaintenancePeriodAvailable(
          standId,
          periodId,
        ),
      );
      return true;
    } catch {
      // Business validation messages are returned and localized by the Orchestrator/ABP pipeline.
      return false;
    }
  }

  canLeaveSync(): boolean {
    return true;
  }

  collect(context: IWizardContext): IStepCreateAreaMaintenanceData {
    const previousStepData =
      context.getStepState<IStepSelectStandsToFuseData>(this.previousStepId)?.data;
    const data: IStepCreateAreaMaintenanceData = {
      sourceStep2Snapshot: this.buildStep2Snapshot(previousStepData),
      resultingStand: this.resultingStand(),
      maintenance: this.toAreaMaintenanceFormValue(this.form.getRawValue()),
      selectedOptions: this.toSelectedOptionsSnapshot(),
      areaDelta: this.areaDelta(),
    };

    context.patchStepState(context.activeStepId(), {
      visited: true,
      valid: this.form.valid,
      completed: true,
      data,
    });

    return data;
  }

  fieldInvalid(field: keyof IAreaMaintenanceFormValue): boolean {
    const control = this.form.get(field);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  formatArea(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return EMPTY_VALUE;
    }

    return value.toFixed(2).replace('.', ',');
  }

  formatSignedArea(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return EMPTY_VALUE;
    }

    const absoluteValue = this.formatArea(Math.abs(value));
    if (value > 0) {
      return `+${absoluteValue}`;
    }

    if (value < 0) {
      return `-${absoluteValue}`;
    }

    return absoluteValue;
  }

  displayValue(value: string | null | undefined): string {
    return value?.trim() ? value : EMPTY_VALUE;
  }

  onPeriodChange(value: SelectValue): void {
    this.syncSelectedOption('period', value, this.periodCatalog, this.periodOptions);
  }

  onReasonChange(value: SelectValue): void {
    this.syncSelectedOption('reason', value, this.reasonCatalog, this.reasonOptions);
  }

  onAdjustmentTypeChange(value: SelectValue): void {
    this.syncSelectedOption(
      'adjustmentType',
      value,
      this.adjustmentTypeCatalog,
      this.adjustmentTypeOptions,
    );
  }

  private createAreaMaintenanceForm() {
    return this.formBuilder.group({
      period: [null as string | null, [Validators.required]],
      reason: [null as string | null, [Validators.required]],
      adjustmentType: [null as string | null, [Validators.required]],
      details: [''],
      isAreaUpdated: [{ value: true, disabled: true }],
    });
  }

  private loadSelectCatalog(
    params: ZnSelectQueryParams | undefined,
    loader: (normalizedParams: ZnSelectQueryParams) => Observable<{
      items: IAreaMaintenanceSelectOption[];
      totalCount: number;
    }>,
    cache: Map<string, IAreaMaintenanceSelectOption>,
  ): Observable<{ items: IAreaMaintenanceSelectOption[]; totalCount: number }> {
    return loader({
      ...params,
      maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
    }).pipe(
      tap(response => {
        response.items.forEach(item => {
          cache.set(item.key, item);
        });
      }),
    );
  }

  private buildInitialFormValue(
    value?: IAreaMaintenanceFormValue,
  ): IAreaMaintenanceFormValue {
    return {
      period: value?.period ?? null,
      reason: value?.reason ?? null,
      adjustmentType: value?.adjustmentType ?? null,
      details: value?.details ?? '',
      isAreaUpdated: true,
    };
  }

  private toAreaMaintenanceFormValue(
    rawValue: Partial<IAreaMaintenanceFormValue>,
  ): IAreaMaintenanceFormValue {
    return {
      period: rawValue.period ?? null,
      reason: rawValue.reason ?? null,
      adjustmentType: rawValue.adjustmentType ?? null,
      details: rawValue.details?.trim() ?? '',
      isAreaUpdated: true,
    };
  }

  private restoreSelectedOptions(value?: IAreaMaintenanceSelectedOptions): void {
    this.periodCatalog.clear();
    this.reasonCatalog.clear();
    this.adjustmentTypeCatalog.clear();

    this.restoreSelectedOption(value?.period, this.periodCatalog, this.periodOptions);
    this.restoreSelectedOption(value?.reason, this.reasonCatalog, this.reasonOptions);
    this.restoreSelectedOption(
      value?.adjustmentType,
      this.adjustmentTypeCatalog,
      this.adjustmentTypeOptions,
    );
  }

  private restoreSelectedOption(
    option: IAreaMaintenanceSelectOption | null | undefined,
    cache: Map<string, IAreaMaintenanceSelectOption>,
    optionsSignal: WritableSignal<IAreaMaintenanceSelectOption[]>,
  ): void {
    if (!option) {
      optionsSignal.set([]);
      return;
    }

    cache.set(option.key, option);
    optionsSignal.set([option]);
  }

  private syncSelectedOption(
    field: keyof Pick<IAreaMaintenanceFormValue, 'period' | 'reason' | 'adjustmentType'>,
    value: SelectValue,
    cache: Map<string, IAreaMaintenanceSelectOption>,
    optionsSignal: WritableSignal<IAreaMaintenanceSelectOption[]>,
  ): void {
    const selectedId = Array.isArray(value) ? (value[0] ?? null) : value;
    this.form.get(field)?.setValue(selectedId, { emitEvent: false });

    if (!selectedId) {
      optionsSignal.set([]);
      return;
    }

    const option = cache.get(selectedId);
    optionsSignal.set(option ? [option] : []);
  }

  private toSelectedOptionsSnapshot(): IAreaMaintenanceSelectedOptions {
    return {
      period: this.periodOptions()[0] ?? null,
      reason: this.reasonOptions()[0] ?? null,
      adjustmentType: this.adjustmentTypeOptions()[0] ?? null,
    };
  }

  private buildStep2Snapshot(value?: IStepSelectStandsToFuseData): string {
    return JSON.stringify({
      establishmentId: value?.establishmentId ?? null,
      selectedStands: (value?.selectedStands ?? []).map(row => ({
        backendId: row.backendId,
        standCycleRotationId: row.standCycleRotationId,
        area: this.roundArea(row.area),
        isPersistedStand: row.isPersistedStand,
      })),
      resultingStand: value?.resultingStand
        ? {
            standBackendId: value.resultingStand.standBackendId ?? null,
            standCycleRotationId: value.resultingStand.standCycleRotationId ?? null,
            currentArea: value.resultingStand.currentArea,
            adjustedArea: this.roundArea(value.resultingStand.adjustedArea),
          }
        : null,
      totalArea: this.roundArea(value?.totalArea ?? 0),
    });
  }

  private validateRequiredForm(): boolean {
    this.form.markAllAsTouched();

    if (this.form.valid) {
      return true;
    }

    this.notifyRequiredFields();
    return false;
  }

  private notifyRequiredFields(): void {
    this.toaster.error(
      `${ZendraLocalizationServices.Geographic}::StandFusion.StepCreateAreaMaintenance.RequiredFields`,
      '',
      { life: DEFAULT_TOASTER_LIFE },
    );
  }

  private roundArea(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }
}

